/**
 * BuildingReviewPanel — Formulario de revisión manual de un edificio
 *
 * @remarks
 * Se renderiza dentro del tab Buildings del LeftPanel cuando el usuario
 * hace click en "Revisar" desde el popup del mapa.
 *
 * Operaciones:
 * - Carga el estado de revisión actual (GET review)
 * - Permite editar decisión poblacional, función urbana, niveles, nota y estado revisado
 * - Guarda con PATCH (upsert)
 * - Resetea con DELETE (previo confirm inline)
 *
 * Invariantes:
 * - No muta building_attributes ni building_centroids.
 * - No calcula población.
 * - Muestra dato original cuando hay override activo.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Save, RotateCcw, AlertTriangle, CheckCircle2, Pencil } from 'lucide-react';
import {
  buildingReviewApi,
  type BuildingReviewResponse,
  type ManualPopulationStatus,
  type ManualFunctionalClass,
} from './building-review-api';
import {
  labelOf,
  FUNCTIONAL_CLASS_LABELS,
  MANUAL_POPULATION_STATUS_LABELS,
  MANUAL_POPULATION_STATUS_HINTS,
} from './building-v12-labels';

// ─── Tipos internos ───────────────────────────────────────────────────────────

interface FormState {
  manualPopulationStatus: ManualPopulationStatus;
  manualFunctionalClass:  ManualFunctionalClass | null;
  manualLevels:           number | null;
  isUserReviewed:         boolean;
  reviewNote:             string;
}

interface Props {
  buildingId:         string;
  projectId:          string;
  initialProperties:  Record<string, unknown>;
  onClose:            () => void;
  /** Llamado tras guardar o resetear para recargar el bbox del mapa */
  onSaved:            () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildingIdShort(id: string): string {
  return id.substring(0, 8).toUpperCase();
}

function levelsLabel(n: number | null): string {
  if (n == null) return '—';
  return `${n} piso${n !== 1 ? 's' : ''}`;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export function BuildingReviewPanel({
  buildingId,
  projectId,
  initialProperties,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const [review,        setReview]        = useState<BuildingReviewResponse | null>(null);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [confirmReset,  setConfirmReset]  = useState(false);
  const [savedOk,       setSavedOk]       = useState(false);

  const [form, setForm] = useState<FormState>({
    manualPopulationStatus: 'auto',
    manualFunctionalClass:  null,
    manualLevels:           null,
    isUserReviewed:         false,
    reviewNote:             '',
  });

  // ── Cargar revisión actual ──────────────────────────────────────────────────

  const loadReview = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await buildingReviewApi.getReview(projectId, buildingId);
      setReview(data);
      setForm({
        manualPopulationStatus: data.manualPopulationStatus,
        manualFunctionalClass:  data.manualFunctionalClass,
        manualLevels:           data.manualLevels,
        isUserReviewed:         data.isUserReviewed,
        reviewNote:             data.reviewNote ?? '',
      });
    } catch {
      setError('No se pudo cargar la revisión.');
    } finally {
      setLoading(false);
    }
  }, [projectId, buildingId]);

  useEffect(() => { void loadReview(); }, [loadReview]);

  // ── Guardar ────────────────────────────────────────────────────────────────

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedOk(false);
    try {
      await buildingReviewApi.patchReview(projectId, buildingId, {
        manualPopulationStatus: form.manualPopulationStatus,
        manualFunctionalClass:  form.manualFunctionalClass,
        manualLevels:           form.manualLevels,
        isUserReviewed:         form.isUserReviewed,
        reviewNote:             form.reviewNote.trim() || null,
      });
      setSavedOk(true);
      onSaved();
      setTimeout(() => setSavedOk(false), 2000);
      // Recargar para sincronizar estado
      await loadReview();
    } catch {
      setError('Error al guardar. Intentá nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  // ── Resetear ───────────────────────────────────────────────────────────────

  const handleReset = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await buildingReviewApi.deleteReview(projectId, buildingId);
      onSaved();
      onClose();
    } catch {
      setError('Error al resetear. Intentá nuevamente.');
      setSaving(false);
    }
  };

  // ── Niveles: cambio incremental ────────────────────────────────────────────

  const stepLevels = (delta: number): void => {
    setForm((f) => {
      const current = f.manualLevels ?? 0;
      const next = Math.max(1, Math.min(120, current + delta));
      return { ...f, manualLevels: next };
    });
  };

  // ── Datos originales (de las properties del GeoJSON) ──────────────────────

  const origFuncClass = initialProperties['functionalClass'] as string | null;
  const origLevels    = initialProperties['levelsEstimated'] as number | null;

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="brp-container">
        <div className="brp-header">
          <span className="brp-title">Cargando revisión…</span>
          <button className="brp-close-btn" onClick={onClose} title="Cerrar"><X size={14} /></button>
        </div>
        <div className="brp-loading">Cargando…</div>
      </div>
    );
  }

  const hasOverride = form.manualFunctionalClass !== null || form.manualLevels !== null || form.manualPopulationStatus !== 'auto';

  return (
    <div className="brp-container">

      {/* ── Header ── */}
      <div className="brp-header">
        <span className="brp-title">
          <Pencil size={12} />
          Revisión
          <span className="brp-id">
            {(initialProperties['displayCode'] as string | null | undefined)
              ?? buildingIdShort(buildingId)}
          </span>
        </span>
        <div className="brp-badges">
          {review?.isUserReviewed && (
            <span className="brp-badge brp-badge--reviewed">
              <CheckCircle2 size={10} /> Revisado
            </span>
          )}
          {hasOverride && (
            <span className="brp-badge brp-badge--edited">
              <Pencil size={10} /> Editado
            </span>
          )}
          {initialProperties['requiresReview'] === true && !review?.isUserReviewed && (
            <span className="brp-badge brp-badge--suggested">
              <AlertTriangle size={10} /> Revisión sugerida
            </span>
          )}
        </div>
        <button className="brp-close-btn" onClick={onClose} title="Cerrar"><X size={14} /></button>
      </div>

      {/* ── Confirm reset ── */}
      {confirmReset && (
        <div className="brp-confirm">
          <span>¿Descartar la revisión manual y volver al criterio automático?</span>
          <div className="brp-confirm-actions">
            <button
              className="brp-btn brp-btn--danger"
              onClick={handleReset}
              disabled={saving}
            >
              {saving ? 'Reseteando…' : 'Sí, resetear'}
            </button>
            <button className="brp-btn brp-btn--ghost" onClick={() => setConfirmReset(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Formulario ── */}
      {!confirmReset && (
        <div className="brp-form">

          {/* Cálculo poblacional */}
          <div className="brp-field">
            <label className="brp-label">Cálculo poblacional</label>
            <div className="brp-radio-group">
              {(['auto', 'include', 'exclude'] as ManualPopulationStatus[]).map((val) => (
                <label key={val} className={`brp-radio-option${form.manualPopulationStatus === val ? ' is-active' : ''}`}>
                  <input
                    type="radio"
                    name="manualPopulationStatus"
                    value={val}
                    checked={form.manualPopulationStatus === val}
                    onChange={() => setForm((f) => ({ ...f, manualPopulationStatus: val }))}
                  />
                  <span>{MANUAL_POPULATION_STATUS_LABELS[val]}</span>
                </label>
              ))}
            </div>
            <div className="brp-hint">
              {MANUAL_POPULATION_STATUS_HINTS[form.manualPopulationStatus]}
            </div>
          </div>

          {/* Función urbana */}
          <div className="brp-field">
            <label className="brp-label">
              Función urbana
              <span className="brp-label-optional">opcional</span>
            </label>
            <select
              className="brp-select"
              value={form.manualFunctionalClass ?? ''}
              onChange={(e) => {
                const v = e.target.value as ManualFunctionalClass | '';
                setForm((f) => ({ ...f, manualFunctionalClass: v === '' ? null : v }));
              }}
            >
              <option value="">No clasificada</option>
              <option value="residential">Residencial</option>
              <option value="non_residential">No residencial</option>
              <option value="mixed">Mixta</option>
            </select>
            {form.manualFunctionalClass === null && (
              <div className="brp-original-note">No define uso residencial.</div>
            )}
            {form.manualFunctionalClass !== null && origFuncClass !== null && (
              <div className="brp-original-note">
                Dato original: {labelOf(FUNCTIONAL_CLASS_LABELS, origFuncClass, 'No clasificada')}
              </div>
            )}
          </div>

          {/* Niveles */}
          <div className="brp-field">
            <label className="brp-label">
              Niveles
              <span className="brp-label-optional">opcional</span>
            </label>
            <div className="brp-levels-row">
              <button
                className="brp-levels-btn"
                type="button"
                onClick={() => stepLevels(-1)}
                disabled={form.manualLevels === null || form.manualLevels <= 1}
                title="Restar"
              >−</button>
              <input
                type="number"
                className="brp-levels-input"
                min={1}
                max={120}
                value={form.manualLevels ?? ''}
                placeholder="—"
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setForm((f) => ({ ...f, manualLevels: isNaN(v) ? null : Math.max(1, Math.min(120, v)) }));
                }}
              />
              <button
                className="brp-levels-btn"
                type="button"
                onClick={() => stepLevels(1)}
                disabled={form.manualLevels !== null && form.manualLevels >= 120}
                title="Sumar"
              >+</button>
              {form.manualLevels !== null && (
                <button
                  className="brp-levels-clear"
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, manualLevels: null }))}
                  title="Quitar override"
                >×</button>
              )}
            </div>
            {form.manualLevels !== null && origLevels !== null && (
              <div className="brp-original-note">
                Dato original: {levelsLabel(origLevels)} (estimado)
              </div>
            )}
          </div>

          {/* Marcar como revisado */}
          <div className="brp-field brp-field--inline">
            <label className="brp-checkbox-label">
              <input
                type="checkbox"
                checked={form.isUserReviewed}
                onChange={(e) => setForm((f) => ({ ...f, isUserReviewed: e.target.checked }))}
              />
              <span>Marcar como revisado</span>
            </label>
          </div>

          {/* Nota */}
          <div className="brp-field">
            <label className="brp-label">
              Nota
              <span className="brp-label-optional">opcional</span>
            </label>
            <textarea
              className="brp-textarea"
              rows={2}
              maxLength={2000}
              placeholder="Observaciones sobre este edificio…"
              value={form.reviewNote}
              onChange={(e) => setForm((f) => ({ ...f, reviewNote: e.target.value }))}
            />
          </div>

          {/* Error */}
          {error && <div className="brp-error">{error}</div>}

          {/* Confirmación guardado */}
          {savedOk && (
            <div className="brp-success">
              <CheckCircle2 size={12} /> Guardado correctamente
            </div>
          )}

          {/* Acciones */}
          <div className="brp-actions">
            <button
              className="brp-btn brp-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              <Save size={12} />
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            {review?.hasReview && (
              <button
                className="brp-btn brp-btn--ghost brp-btn--warn"
                onClick={() => setConfirmReset(true)}
                disabled={saving}
                title="Descartar revisión manual"
              >
                <RotateCcw size={12} />
                Resetear
              </button>
            )}
            <button className="brp-btn brp-btn--ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
          </div>

        </div>
      )}
    </div>
  );
}
